import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Form, Input, notification } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';

import { login, selectors } from 'state';
import { HOST } from 'settings';

import VegaLogo from 'assets/images/vega_logo_transparent_oficial.png';

import './loginV2.css';

/**
 * Login v2 — modern split-screen redesign of src/views/Auth/Login.tsx.
 *
 * BEHAVIOR IS IDENTICAL to the classic login by design; only the presentation
 * changed. Kept exactly as-is vs v1:
 *   - reads selectors.auth.error / selectors.auth.waiting
 *   - dispatch(login({ username, password })) on validated submit
 *   - notification.warning('Invalid credentials') on error
 *   - same required-field rules and the same (verbatim) validation messages
 *   - same brand name resolution from HOST
 * The classic component is untouched; this renders only when the v2 flag is on.
 */
const LoginV2: React.FC = () => {
  const dispatch = useDispatch();
  const error = useSelector(selectors.auth.error);
  const waiting = useSelector(selectors.auth.waiting);

  const [form] = Form.useForm();

  React.useEffect(() => {
    if (error) notification.warning({ message: 'Invalid credentials' });
  }, [error]);

  const name = HOST == 'VEGASIMULATOR' ? 'Vegasimulator' : 'Traderion';

  return (
    <div className="lv2-root">
      {/* Left: brand showcase */}
      <div className="lv2-brand">
        <div className="lv2-orb a" />
        <div className="lv2-orb b" />

        <div className="lv2-brand-top">
          <img className="lv2-brand-logo" src={VegaLogo} alt={name} />
        </div>

        <div className="lv2-brand-hero">
          <div className="lv2-brand-eyebrow">Portfolio Manager</div>
          <h1>
            Trade smarter.
            <br />
            Train sharper.
          </h1>
          <p>
            Your portfolio management simulator. Live markets, real decisions,
            measurable growth.
          </p>
        </div>

        <div className="lv2-brand-foot">
          <span>© {name}</span>
        </div>
      </div>

      {/* Right: form */}
      <div className="lv2-form-panel">
        <div className="lv2-card">
          <div className="lv2-card-head">
            <h2>Welcome back</h2>
            <p>Please enter your credentials to continue.</p>
          </div>

          <Form
            form={form}
            name="normal_login_v2"
            initialValues={{ username: '', password: '' }}
            onFinish={() => {
              const data = form.getFieldsValue() as {
                username: string;
                password: string;
              };
              dispatch(login(data));
            }}
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              name="username"
              label={<span className="lv2-label">Username</span>}
              rules={[
                {
                  required: true,
                  message: 'Please input a your username!',
                },
              ]}
            >
              <Input
                size="large"
                prefix={<UserOutlined className="lv2-affix-icon" />}
                placeholder="Enter your username"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span className="lv2-label">Password</span>}
              rules={[
                { required: true, message: 'Please input your Password!' },
              ]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined className="lv2-affix-icon" />}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                className="lv2-submit"
                loading={waiting}
              >
                Log in
              </Button>
            </Form.Item>
          </Form>

          <div className="lv2-foot-note">
            Secure sign-in · {name} Portfolio Manager
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginV2;
